import { useState } from "react";
import { CarrierConfig, CARRIER_STYLES, Carrier } from "../types";

interface Props {
  carriers: CarrierConfig[];
  onChange: (carriers: CarrierConfig[]) => void;
  adminNumbers?: Record<string, string>;
}

function maskPhone(phone: string): string {
  const digits = phone.replace(/[^0-9]/g, "");
  if (digits.length === 11) {
    return digits.slice(0, 3) + "-****-" + digits.slice(7);
  }
  if (digits.length === 10) {
    return digits.slice(0, 3) + "-****-" + digits.slice(6);
  }
  if (digits.length >= 7) {
    const mid = Math.floor(digits.length / 2);
    return digits.slice(0, mid - 2) + "****" + digits.slice(mid + 2);
  }
  return phone;
}

const CARRIER_ICONS: Record<Carrier, string> = {
  SKT: "ri-signal-tower-line",
  KT: "ri-wifi-line",
  "LGU+": "ri-smartphone-line",
};

export default function CarrierSelector({ carriers, onChange, adminNumbers }: Props) {
  const hasAdminNumbers = adminNumbers && Object.values(adminNumbers).some((p) => p.trim());
  const [focusedCarrier, setFocusedCarrier] = useState<Carrier | null>(null);

  const updatePhone = (carrier: Carrier, phone: string) => {
    if (hasAdminNumbers) return;
    onChange(
      carriers.map((c) => (c.carrier === carrier ? { ...c, phone } : c))
    );
  };

  const toggleCarrier = (carrier: Carrier) => {
    onChange(
      carriers.map((c) => (c.carrier === carrier ? { ...c, selected: !c.selected } : c))
    );
  };

  const selectedCount = carriers.filter((c) => c.selected).length;

  return (
    <div className="space-y-3">
      {/* 선택 안내 */}
      <div className="flex items-center justify-between">
        <span className="text-gray-500 text-xs font-medium">발송 대상 통신사</span>
        <span className="text-gray-400 text-xs whitespace-nowrap">
          선택 {selectedCount} / {carriers.length}
        </span>
      </div>

      {/* Carrier Cards */}
      <div className="space-y-2.5">
        {carriers.map((c) => {
          const style = CARRIER_STYLES[c.carrier];
          const adminPhone = adminNumbers?.[c.carrier];
          const isAdminNumber = !!(hasAdminNumbers && adminPhone?.trim());
          const displayPhone = isAdminNumber ? maskPhone(adminPhone!) : c.phone;
          const isFocused = focusedCarrier === c.carrier;

          return (
            <div
              key={c.carrier}
              onClick={() => toggleCarrier(c.carrier)}
              className={`group relative flex items-center gap-3 px-4 py-3.5 rounded-xl border transition-all duration-200 cursor-pointer ${
                c.selected
                  ? `${style.border} ${style.bg}`
                  : "border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]"
              }`}
            >
              {/* Selection indicator */}
              <div
                className={`w-6 h-6 flex items-center justify-center rounded-full border-2 transition-all duration-200 shrink-0 ${
                  c.selected
                    ? `${style.border.replace("/30", "/50")} ${style.bg}`
                    : "border-white/20 bg-white/[0.03]"
                }`}
              >
                <i
                  className={`ri-check-line text-xs transition-all duration-200 ${
                    c.selected ? `${style.check} opacity-100 scale-100` : "opacity-0 scale-75"
                  }`}
                ></i>
              </div>

              {/* Carrier Icon */}
              <div
                className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 shrink-0 ${
                  c.selected ? style.bg.replace("/10", "/20") : "bg-white/[0.04]"
                }`}
              >
                <i
                  className={`${CARRIER_ICONS[c.carrier]} text-base transition-all duration-200 ${
                    c.selected ? style.text : "text-gray-500"
                  }`}
                ></i>
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span
                    className={`text-sm font-bold transition-colors duration-200 ${
                      c.selected ? style.label : "text-gray-400"
                    }`}
                  >
                    {c.carrier}
                  </span>
                  {isAdminNumber && (
                    <span className="text-[10px] text-gray-500 bg-white/[0.06] px-1.5 py-0.5 rounded border border-white/[0.08] whitespace-nowrap">
                      관리자 지정
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <i className="ri-phone-line text-gray-600 text-[10px]"></i>
                  <input
                    type="text"
                    value={displayPhone}
                    readOnly={isAdminNumber}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={() => setFocusedCarrier(c.carrier)}
                    onBlur={() => setFocusedCarrier(null)}
                    onChange={(e) => updatePhone(c.carrier, e.target.value)}
                    placeholder={isAdminNumber ? "번호 미등록" : "010-0000-0000"}
                    className={`bg-transparent text-xs focus:outline-none transition-all ${
                      isAdminNumber
                        ? "text-gray-400 cursor-default w-full"
                        : "text-gray-300 cursor-text w-full placeholder-gray-600 focus:text-white"
                    } ${isFocused ? "border-b border-white/10" : ""}`}
                  />
                </div>
              </div>

              {/* Selected glow effect */}
              {c.selected && (
                <div
                  className={`absolute inset-0 rounded-xl pointer-events-none opacity-30`}
                  style={{
                    boxShadow: `inset 0 0 20px -10px var(--tw-colors-${style.dot.replace("bg-", "").replace("-400", "-500")})`,
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Hint */}
      <p className="text-gray-600 text-[10px] flex items-center gap-1">
        <i className="ri-information-line text-gray-500"></i>
        통신사를 클릭해 발송 대상을 선택/해제할 수 있습니다
      </p>
    </div>
  );
}